import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import _ from 'underscore';
import PropTypes from 'prop-types';

import Datepicker from './Datepicker';
import { getMillisFromDateString } from 'utils/time';

import { LabelList } from '@shoflo/polymer/components/label/LabelList';
import { LabelPicker } from '@shoflo/polymer/components/label/LabelPicker';
import { UserPicker } from '@shoflo/polymer/components/UserPicker/UserPicker';
import { EventValidator } from '@shoflo/polymer/validators/EventValidator/EventValidator';
import { TimeZonePicker, DefaultTimeZone } from '@shoflo/polymer/components/TimeZonePicker/TimeZonePicker';
import { useFormState } from 'react-use-form-state';

import {
  Row,
  Col,
  Input,
  Label,
  Form,
  FormGroup,
  FormFeedback,
} from 'reactstrap';

import { Button } from '@shoflo/polymer/components/Button/Button';
import PhotoInput from 'components/PhotoInput';
import styled from 'styled-components';
import StyledFormLabel from 'components/form/StyledFormLabel';
import Tooltip from 'components/Tooltip';

const StyledLabelGroup = styled(FormGroup)`
  display: flex;
`;

const OwnerWrapper = styled.div`
  display: flex;
  align-items: center;

  span {
    display: flex;
    align-items: center;

    select {
      margin-left: 10px;
    }
  }
`;

export const handleDateChange = (
  dateType,
  setStartDate,
  setEndDate,
  dateFormat,
  date,
) => {
  if (dateType === 'startDate') {
    if (date) {
      setStartDate(moment(date).format(dateFormat));
      setEndDate(moment(date).format(dateFormat));
    } else {
      setStartDate(undefined);
    }
  } else {
    setEndDate(date ? moment(date).format(dateFormat) : date);
  }
};

const CreateForm = ({
  onSubmit,
  onCancel,
  onLabelCreated,
  owners,
  labels,
  event,
  team,
  user,
}) => {
  const eventTitleRef = useRef();

  useEffect(() => {
    eventTitleRef.current.focus();
  }, []);

  const previousEventLabelIds = event.labels
    ? event.labels.map((label) => label._id)
    : [];

  const [selectedLabelIds, setSelectedLabelIds] = useState(
    previousEventLabelIds,
  );

  const [photoFile, setPhotoFile] = useState();

  const dateFormat = team.dateFormat || 'MM/DD/YYYY';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDate, setStartDate] = useState(moment().format(dateFormat));
  const [endDate, setEndDate] = useState(
    moment().add(7, 'days').format(dateFormat),
  );

  const previousEventOwner = event.owner
    ? owners.find((o) => o._id === event.owner._id)
    : null;

  const defaultOwner =
    owners.find((o) => o._id === (user && user._id)) || owners[0];

  const [formState, { label, text, select, raw, radio }] = useFormState({
    title: event.title,
    venue: event.venue || '',
    twentyFourHourClock: event.twentyFourHourClock
      ? event.twentyFourHourClock.toString()
      : 'false',
    timeZone: event.timeZone || DefaultTimeZone,
    owner: previousEventOwner ? previousEventOwner._id : defaultOwner._id,
  });

  const { values } = formState;

  return (
    <div>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          setIsSubmitting(true);
          const { timeZone } = values;
          onSubmit(
            {
              ...formState.values,
              startOn: getMillisFromDateString(startDate, dateFormat, timeZone),
              endOn: getMillisFromDateString(endDate, dateFormat, timeZone),
              twentyFourHourClock:
                formState.values.twentyFourHourClock === 'true',
            },
            photoFile,
            selectedLabelIds,
          );
        }}
      >
        <FormGroup>
          <StyledFormLabel htmlFor="eventTitle" {...label('title')}>
            Name
          </StyledFormLabel>
          <Tooltip
            id="event-create-form-name"
            title="This text will identify this Event"
          />
          <Input
            id="eventTitle"
            data-testid="eventTitle"
            innerRef={eventTitleRef}
            placeholder="e.g. Global Summit 2019"
            aria-describedby="tip-event-create-form-name"
            {...text({
              name: 'title',
              validate: EventValidator.title,
              validateOnBlur: false,
              touchOnChange: true,
            })}
            invalid={formState.touched.title && !formState.validity.title}
            required
          />
          {formState.validity.title === false && (
            <FormFeedback>
              Uh oh! Event names must be at least 2 characters.
            </FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <StyledFormLabel {...label('venue')}>Location</StyledFormLabel>
          <Tooltip
            id="event-create-form-location"
            title="Where is this Event taking place"
          />
          <Input
            data-testid="eventLocation"
            aria-labelledby="eventLocationInput"
            aria-describedby="tip-event-create-form-location"
            {...text({
              name: 'venue',
              validateOnBlur: false,
              touchOnChange: true,
            })}
          />
        </FormGroup>

        <Row form>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel
                {...label('startOn')}
                htmlFor="startOn-dayPickerInput"
              >
                Start Date
              </StyledFormLabel>
              <Datepicker
                format={dateFormat}
                title="startOn"
                value={startDate}
                onChange={handleDateChange.bind(
                  null,
                  'startDate',
                  setStartDate,
                  setEndDate,
                  dateFormat,
                )}
                tooltipProps={{
                  id: 'event-create-form-start-date',
                  title: 'When does this Event begin?',
                }}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel
                {...label('endOn')}
                htmlFor="endOn-dayPickerInput"
              >
                End Date
              </StyledFormLabel>
              <Datepicker
                format={dateFormat}
                timeZone={values.timeZone}
                title="endOn"
                value={endDate}
                disablePastDates={true}
                onChange={handleDateChange.bind(
                  null,
                  'endDate',
                  setStartDate,
                  setEndDate,
                  dateFormat,
                )}
                tooltipProps={{
                  id: 'event-create-form-end-date',
                  title:
                    'When does this Event End? Note: that this date will be used to automatically archive the Event',
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row form>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel htmlFor="timeZone">Time Zone</StyledFormLabel>
              <Tooltip
                id="event-create-form-timezone"
                title="This setting will affect the time of day clock for all files in this Event"
              />
              <FormGroup>
                <TimeZonePicker
                  {...select({
                    name: 'timeZone',
                  })}
                  aria-describedby="tip-event-create-form-timezone"
                />
              </FormGroup>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup check>
              <Tooltip
                id="event-create-form-time-format"
                title="This setting will affect the time of day clock for all files in this Event"
              />
              <StyledFormLabel aria-describedby="tip-event-create-form-time-format">
                Time Format
              </StyledFormLabel>
              <br />
              <Label check>
                <Input
                  {...radio({ name: 'twentyFourHourClock', value: false })}
                  data-testid="12HourButton"
                  id="12hour"
                  checked={values.twentyFourHourClock === 'false'}
                />{' '}
                12-hour
              </Label>
              <br />
              <Label check>
                <Input
                  {...radio({ name: 'twentyFourHourClock', value: true })}
                  data-testid="24HourButton"
                  id="24hour"
                  checked={values.twentyFourHourClock === 'true'}
                />{' '}
                24-hour
              </Label>
            </FormGroup>
          </Col>
        </Row>
        <Tooltip
          id="event-create-form-labels"
          title="A colorful label used to quickly identify a property of an event"
        />
        <StyledFormLabel aria-describedby="tip-event-create-form-labels">
          Label(s)
        </StyledFormLabel>
        <StyledLabelGroup>
          <LabelList
            labels={selectedLabelIds.map((id) => {
              const label = _.find(labels, (label) => label._id === id);
              return {
                ...label,
                onClick: () => {
                  setSelectedLabelIds(
                    selectedLabelIds.filter((id) => label._id !== id),
                  );
                },
              };
            })}
          />
          <LabelPicker
            labels={labels}
            selectedLabelIds={selectedLabelIds}
            onCreate={onLabelCreated}
            onChange={setSelectedLabelIds}
          />
        </StyledLabelGroup>
        <Row form>
          <Col md={6}>
            <PhotoInput
              label="Event branding"
              imageUri={event.photo}
              onChange={setPhotoFile}
              type="event"
              tooltipProps={{
                id: 'event-create-form-branding',
                placement: 'bottom-start',
                title:
                  'Select the image which will identify this event in Shoflo and on Printouts',
              }}
            />
          </Col>
          <Col md={6}>
            <Tooltip
              id="event-create-form-owner"
              title="Who is the point person for this Event?"
            />
            <StyledFormLabel aria-describedby="tip-event-create-form-owner">
              Owner
            </StyledFormLabel>
            <OwnerWrapper>
              <UserPicker
                {...raw({
                  name: 'owner',
                })}
                id="owner"
                users={owners}
              />
            </OwnerWrapper>
          </Col>
        </Row>

        <div className="float-end modal-footer">
          <Button
            data-testid="createButton"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>{' '}
          <Button
            data-testid="cancelButton"
            color="secondary"
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

CreateForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onLabelCreated: PropTypes.func.isRequired,
  labels: PropTypes.array.isRequired,
  owners: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
};

export default CreateForm;
